import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  overlayClassName?: string;
}) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        overlayClassName,
        "aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:fixed aero:inset-0 aero:z-50 aero:bg-black/50",
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  overlayClassName,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  overlayClassName?: string;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay overlayClassName={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "aero:bg-background aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:data-[state=closed]:zoom-out-95 aero:data-[state=open]:zoom-in-95 aero:fixed aero:top-[50%] aero:left-[50%] aero:z-50 aero:grid aero:w-full aero:max-w-[calc(100%-2rem)] aero:translate-x-[-50%] aero:translate-y-[-50%] aero:gap-4 aero:!rounded-lg aero:border aero:p-6 aero:shadow-lg aero:duration-200 aero:sm:max-w-lg",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="aero:ring-offset-background aero:focus:ring-ring aero:data-[state=open]:bg-accent aero:data-[state=open]:text-muted-foreground aero:absolute aero:top-4 aero:right-4 aero:!rounded-xs aero:opacity-70 aero:transition-opacity aero:hover:opacity-100 aero:focus:ring-2 aero:focus:ring-offset-2 aero:focus:outline-hidden aero:disabled:pointer-events-none aero:[&_svg]:pointer-events-none aero:[&_svg]:shrink-0 aero:[&_svg:not([class*='size-'])]:size-4">
          <XIcon />
          <span className="aero:sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "aero:flex aero:flex-col aero:gap-2 aero:text-center aero:sm:text-left",
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "aero:flex aero:flex-col-reverse aero:gap-2 aero:sm:flex-row aero:sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "aero:text-lg aero:leading-none aero:font-semibold",
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("aero:text-muted-foreground aero:text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
